import React, { useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { TimeSeriesBucket } from '../hooks/useAnalyticsData';

interface AnalyticsVolumeChartProps {
  timeSeriesBuckets: TimeSeriesBucket[];
  timeRange: '24h' | '7d' | '30d';
  totalSubmissions: number;
  spamSubmissions: number;
}

// Helpers for SVG Area & Line Paths
function createLinePath(values: number[], width: number, height: number, maxVal: number): string {
  if (values.length === 0) return '';
  const step = width / (values.length - 1);
  return values.reduce((acc, val, i) => {
    const x = i * step;
    const y = height - (val / maxVal) * (height - 20) - 10;
    return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
  }, '');
}

function createAreaPath(values: number[], width: number, height: number, maxVal: number): string {
  if (values.length === 0) return '';
  const line = createLinePath(values, width, height, maxVal);
  return `${line} L ${width} ${height} L 0 ${height} Z`;
}

export const AnalyticsVolumeChart: React.FC<AnalyticsVolumeChartProps> = ({
  timeSeriesBuckets,
  timeRange,
  totalSubmissions,
  spamSubmissions,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const maxVal = Math.max(1, ...timeSeriesBuckets.map((b) => Math.max(b.valid, b.spam)));
  const hasData = totalSubmissions > 0 && timeSeriesBuckets.some((b) => b.total > 0);
  const activeHoveredBucket = hoveredIndex !== null ? timeSeriesBuckets[hoveredIndex] : null;
  const step = timeSeriesBuckets.length > 1 ? 800 / (timeSeriesBuckets.length - 1) : 800;

  return (
    <Card className="p-5 bg-cf-surface border-cf-border shadow-xs">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-cf-textPrimary">Ingestion Volume Over Time</h3>
          <p className="text-xs text-cf-textSecondary mt-0.5">
            Valid form submissions vs honeypot spam traps across {timeRange.toUpperCase()}.
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <span className="text-cf-textSecondary">Valid Traffic</span>
          </div>
          {spamSubmissions > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
              <span className="text-cf-textSecondary">Spam Quarantined</span>
            </div>
          )}
        </div>
      </div>

      {/* SVG High-Performance Area Chart */}
      <div className="w-full relative">
        <div className="h-56 w-full relative bg-cf-bg rounded border border-cf-border overflow-hidden">
          <svg
            className="w-full h-full"
            viewBox="0 0 800 200"
            preserveAspectRatio="none"
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <defs>
              <linearGradient id="validGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#10B981" stopOpacity="0.0" />
              </linearGradient>
              <linearGradient id="spamGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#EF4444" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#EF4444" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Grid horizontal hairline lines */}
            <line x1="0" y1="50" x2="800" y2="50" stroke="currentColor" className="text-cf-border" strokeWidth="1" strokeDasharray="4 4" />
            <line x1="0" y1="100" x2="800" y2="100" stroke="currentColor" className="text-cf-border" strokeWidth="1" strokeDasharray="4 4" />
            <line x1="0" y1="150" x2="800" y2="150" stroke="currentColor" className="text-cf-border" strokeWidth="1" strokeDasharray="4 4" />
            <line x1="0" y1="190" x2="800" y2="190" stroke="currentColor" className="text-cf-border" strokeWidth="1" />

            {hasData && (
              <>
                {/* Area Path for Valid traffic */}
                <path
                  d={createAreaPath(timeSeriesBuckets.map((b) => b.valid), 800, 200, maxVal)}
                  fill="url(#validGradient)"
                />
                {/* Stroke Line for Valid traffic */}
                <path
                  d={createLinePath(timeSeriesBuckets.map((b) => b.valid), 800, 200, maxVal)}
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Area Path for Spam traffic */}
                {spamSubmissions > 0 && (
                  <>
                    <path
                      d={createAreaPath(timeSeriesBuckets.map((b) => b.spam), 800, 200, maxVal)}
                      fill="url(#spamGradient)"
                    />
                    <path
                      d={createLinePath(timeSeriesBuckets.map((b) => b.spam), 800, 200, maxVal)}
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </>
                )}

                {/* Point circles */}
                {timeSeriesBuckets.map((b, idx) => {
                  if (b.total === 0) return null;
                  const x = idx * step;
                  const y = 200 - (b.valid / maxVal) * 170 - 15;
                  return (
                    <circle
                      key={`dot-${idx}`}
                      cx={x}
                      cy={y}
                      r={hoveredIndex === idx ? 5 : 3.5}
                      fill="#10B981"
                      stroke="#0F172A"
                      strokeWidth="1.5"
                      className="transition-all duration-150"
                    />
                  );
                })}
              </>
            )}

            {/* Transparent hit columns for hover interaction */}
            {timeSeriesBuckets.map((_, idx) => {
              const colWidth = 800 / timeSeriesBuckets.length;
              const colX = idx * colWidth;
              return (
                <rect
                  key={`hit-${idx}`}
                  x={colX}
                  y={0}
                  width={colWidth}
                  height={200}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredIndex(idx)}
                />
              );
            })}

            {/* Hover vertical hairline cursor */}
            {hoveredIndex !== null && (
              <line
                x1={hoveredIndex * step}
                y1={15}
                x2={hoveredIndex * step}
                y2={190}
                stroke="#F97316"
                strokeWidth="1.5"
                strokeDasharray="2 2"
              />
            )}
          </svg>

          {/* Zero-traffic empty state overlay */}
          {!hasData && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center pointer-events-none">
              <p className="text-xs font-semibold text-cf-textSecondary">
                No submissions recorded in the past {timeRange === '24h' ? '24 hours' : timeRange === '7d' ? '7 days' : '30 days'}
              </p>
              <p className="text-xs text-cf-textMuted max-w-sm mt-0.5">
                The telemetry pipeline is standing by. Submit form data or post webhooks to populate real-time volume and latency percentiles.
              </p>
            </div>
          )}

          {/* Hover Tooltip Card */}
          {activeHoveredBucket && (
            <div
              className="absolute top-2 pointer-events-none z-10 p-2 rounded bg-cf-surface border border-cf-border shadow-lg text-xs"
              style={{
                left: `${Math.min(85, Math.max(5, (hoveredIndex! / (timeSeriesBuckets.length - 1)) * 100))}%`,
                transform: 'translateX(-50%)',
              }}
            >
              <div className="font-semibold text-cf-textPrimary mb-1">
                {activeHoveredBucket.fullDate}
              </div>
              <div className="space-y-0.5 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-cf-textSecondary">Total:</span>
                  <span className="font-semibold text-cf-textPrimary tabular-nums">
                    {activeHoveredBucket.total.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-emerald-400">Valid:</span>
                  <span className="font-semibold text-emerald-400 tabular-nums">
                    {activeHoveredBucket.valid.toLocaleString()}
                  </span>
                </div>
                {activeHoveredBucket.spam > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-rose-400">Spam:</span>
                    <span className="font-semibold text-rose-400 tabular-nums">
                      {activeHoveredBucket.spam.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* X-Axis labels */}
        <div className="flex justify-between text-xs text-cf-textMuted mt-2 px-1 select-none">
          {timeRange === '24h' ? (
            <>
              <span>24h ago</span>
              <span>18h ago</span>
              <span>12h ago</span>
              <span>6h ago</span>
              <span>Now</span>
            </>
          ) : timeRange === '7d' ? (
            timeSeriesBuckets.map((b) => (
              <span key={b.key}>{b.label}</span>
            ))
          ) : (
            timeSeriesBuckets
              .filter((_, idx) => idx % 5 === 0 || idx === timeSeriesBuckets.length - 1)
              .map((b) => <span key={b.key}>{b.label}</span>)
          )}
        </div>
      </div>
    </Card>
  );
};
